<script lang="ts">
  import { Icon, Input, Textarea, Select, SelectTrigger, SelectContent, SelectItem } from "@price-check/ui";
  import { Phone, Globe } from "lucide";
  import type { Prospect, ProspectPatch, ProspectStatus } from "../../lib/api";
  import Pill from "../../lib/ui/Pill.svelte";
  import { STATUS_LABEL, STATUS_OPTIONS, isDue, telHref, websiteHref } from "../../lib/prospect-labels";

  let { prospect, onSave }: { prospect: Prospect; onSave: (patch: ProspectPatch) => Promise<void> } = $props();

  let notes = $state("");
  $effect(() => {
    notes = prospect.notes ?? "";
  });

  function saveNotes() {
    const next = notes.trim() ? notes : null;
    if (next !== (prospect.notes ?? null)) void onSave({ notes: next });
  }

  function saveDate(e: Event) {
    const v = (e.currentTarget as HTMLInputElement).value;
    if ((v || null) !== prospect.next_follow_up) void onSave({ next_follow_up: v || null });
  }
</script>

<li class="grid grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] gap-4 px-4.5 py-3.5 max-[760px]:grid-cols-1" data-cui={prospect.cui}>
  <div class="min-w-0 flex flex-col gap-1">
    <div class="flex items-center gap-2 min-w-0">
      <strong class="text-sm font-bold truncate" title={prospect.name}>{prospect.name}</strong>
      <Pill tone="indigo">{prospect.score}</Pill>
      {#if !prospect.active}<Pill tone="danger">inactive</Pill>{/if}
      {#if isDue(prospect)}<Pill tone="amber">due</Pill>{/if}
    </div>
    <div class="text-xs text-muted">
      {[prospect.city, prospect.county].filter(Boolean).join(", ") || "—"} · CAEN {prospect.caen} · CUI {prospect.cui}
    </div>
    <div class="flex items-center gap-3 text-sm">
      {#if prospect.phone}
        <a class="inline-flex items-center gap-1.5 font-semibold text-brand-strong" href={telHref(prospect.phone)}>
          <Icon icon={Phone} size={13} />{prospect.phone}
        </a>
      {:else}
        <span class="text-muted text-xs">no phone on record</span>
      {/if}
      {#if prospect.website}
        <a class="inline-flex items-center gap-1 text-xs text-muted" href={websiteHref(prospect.website)} target="_blank" rel="noopener noreferrer">
          <Icon icon={Globe} size={12} />{prospect.website}
        </a>
      {/if}
    </div>
  </div>
  <div class="flex flex-col gap-2">
    <div class="flex gap-2">
      <Select
        type="single"
        value={prospect.status}
        onValueChange={(v) => v && v !== prospect.status && void onSave({ status: v as ProspectStatus })}
      >
        <SelectTrigger size="sm" class="w-40" aria-label="Status for {prospect.name}">{STATUS_LABEL[prospect.status]}</SelectTrigger>
        <SelectContent>
          {#each STATUS_OPTIONS as o (o.value)}<SelectItem value={o.value} label={o.label} />{/each}
        </SelectContent>
      </Select>
      <Input
        type="date"
        class="h-8 w-40"
        value={prospect.next_follow_up ?? ""}
        onchange={saveDate}
        aria-label="Next follow-up for {prospect.name}"
      />
    </div>
    <Textarea
      rows={2}
      class="min-h-0 text-sm"
      placeholder="Notes (who you spoke to, consent given, next step)"
      bind:value={notes}
      onblur={saveNotes}
      aria-label="Notes for {prospect.name}"
    />
  </div>
</li>
